/**
 * components/ui/AppChoice.js
 * ----------------------------------------------------------------------------
 * One choice among a few, as a row of filled segments: TEST or LIVE, the whole
 * amount or part of it. A radio group with buttons for options.
 *
 * WHY A COMPONENT (31 Oct 2026). Two dialogs had each drawn this control by
 * hand (the EFRIS identity editor's environment, the credit note dialog's
 * amount) and both painted the unchosen segment white with a grey border,
 * which is an outlined button, and the dashboard has none (Izi, 13 Oct 2026:
 * every button is filled). The chosen segment is the action navy, or a colour
 * the caller names for a choice that must stand out (LIVE is Lusuku yellow);
 * the others are the light navy tint the pager uses for its other pages, so
 * nothing on the row is hollow and every segment still reads as a control.
 *
 *   <AppChoice
 *     ariaLabel="Environment"
 *     value={mode}
 *     onChange={setMode}
 *     options={[
 *       { value: "TEST", label: "Test (sandbox)" },
 *       { value: "LIVE", label: "Live (URA)", activeBackground: "#F2C811", activeColor: "#0F172A" },
 *     ]}
 *   />
 *
 *   option.extra    rendered after the label on an UNCHOSEN segment only (a
 *                   badge saying what choosing it means)
 *   option.testId   data-testid on that segment
 */
import React from "react";

const TINT = "#e8f0f6";
const ACTION = "var(--lk-action, #083860)";

const AppChoice = ({ options = [], value, onChange, ariaLabel, columns, style }) => (
  <div
    role="radiogroup"
    aria-label={ariaLabel}
    style={{
      display: "grid",
      gridTemplateColumns: `repeat(${columns || options.length || 1}, minmax(0, 1fr))`,
      gap: 8,
      ...style,
    }}
  >
    {options.map((option) => {
      const active = option.value === value;
      return (
        <button
          key={String(option.value)}
          type="button"
          role="radio"
          aria-checked={active}
          onClick={() => onChange && onChange(option.value)}
          data-testid={option.testId}
          style={{
            fontSize: "var(--lk-text-sm, 12px)",
            fontWeight: 700,
            lineHeight: 1.2,
            padding: "9px 10px",
            borderRadius: 8,
            border: "1px solid transparent",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            background: active ? option.activeBackground || ACTION : TINT,
            color: active ? option.activeColor || "#fff" : ACTION,
          }}
        >
          {option.label}
          {!active && option.extra ? option.extra : null}
        </button>
      );
    })}
  </div>
);

export default AppChoice;
